import { AlertCircle } from "lucide-react";

import { LoadingScreen } from "@/components/loading-screen";
import { useTableControl } from "@/components/table-control";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { TabNavigation } from "@/components/ui/tab-navigation";
import { useRecordings } from "@/features/recordings/api/fetch-recordings";
import { useGetRecordingsForDeletionQueue } from "@/features/recordings/api/fetch-recordings-for-deletion-queue";
import { AdminRecordingTable } from "@/features/recordings/components/admin-recording-table";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";

type AdminRecordingsProps = {
  type: "all" | "deletion-queue";
};

export function AdminRecordings({ type }: AdminRecordingsProps) {
  const { pagination, setPagination } = useTableControl({
    initialPageSize: 10,
  });
  const { organisation } = useOrganisation();

  const {
    error: recordingsError,
    isLoading: isRecordingsLoading,
    isFetching: isRecordingsFetching,
    isError: isRecordingsError,
    isSuccess: isRecordingsSuccess,
    data: recordings,
  } = useRecordings(
    {
      pageSize: pagination.pageSize,
      pageIndex: pagination.pageIndex,
      organisationId: organisation?.id ?? "",
    },
    {
      enabled: type === "all",
    },
  );

  const {
    error: recordingsForDeletionQueueError,
    isLoading: isRecordingsForDeletionQueueLoading,
    isFetching: isRecordingsForDeletionQueueFetching,
    isError: isRecordingsForDeletionQueueError,
    isSuccess: isRecordingsForDeletionQueueSuccess,
    data: recordingsForDeletionQueue,
  } = useGetRecordingsForDeletionQueue(
    {
      pageSize: pagination.pageSize,
      pageParam: pagination.pageIndex,
    },
    {
      enabled: type === "deletion-queue",
    },
  );

  const navigationItems = [
    { label: "All Recordings", to: "/admin/recordings/all" },
    { label: "Queue for Deletion", to: "/admin/recordings/deletion-queue" },
  ];

  const error =
    type === "all" ? recordingsError : recordingsForDeletionQueueError;
  const isError =
    type === "all" ? isRecordingsError : isRecordingsForDeletionQueueError;
  const isLoading =
    type === "all" ? isRecordingsLoading : isRecordingsForDeletionQueueLoading;
  const isFetching =
    type === "all"
      ? isRecordingsFetching
      : isRecordingsForDeletionQueueFetching;
  const isSuccess =
    type === "all" ? isRecordingsSuccess : isRecordingsForDeletionQueueSuccess;
  const data = type === "all" ? recordings : recordingsForDeletionQueue;

  return (
    <section>
      <OnboardingHeader
        title="Recordings"
        description="Platform wide analytics"
        className="mb-6"
      />
      <TabNavigation className="mb-6" options={navigationItems} />

      {isLoading && <LoadingScreen />}

      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error?.message ?? "An unexpected error occurred"}
          </AlertDescription>
        </Alert>
      )}

      {isSuccess && data && (
        <AdminRecordingTable
          data={data.items}
          pagination={pagination}
          onPaginationChange={setPagination}
          pageCount={Math.ceil(data.count / pagination.pageSize)}
          isLoading={isFetching}
        />
      )}
    </section>
  );
}
